import CloseIcon from '@mui/icons-material/Close';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar/PickersActionBar';
import React, { useState, useEffect } from 'react';

import {
  AppointmentListDTO,
  AppointmentDetailDTO,
  CompanyWindowListDTO,
  BlockTypeEnum,
  TimezoneAbbreviationEnum,
  Company
} from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { Card } from '_lib/components/Card/Card';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { usePickerActionsContext } from '_lib/components/Pickers/picker.hooks';
import { TimePicker } from '_lib/components/Pickers/TimePicker/TimePicker';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyWindowDisplay } from '_lib/featureComponents/CompanyWindow/CompanyWindowDisplay';
import { getEarliestAppointmentDate, getLatestAppointmentDateInfo } from '_lib/services/appointmentService';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

import { AppointmentWindowDisplay } from '../../AppointmentBookingPane/AppointmentWindowDisplay/AppointmentWindowDisplay';

type BaseAppointmentCardProps = {
  onCloseAppointment?: () => void;
  onSetRequestedTimeMinutes?: (newTime: Date) => void;
  appointment: AppointmentListDTO | AppointmentDetailDTO; // list from prev, detail from hold (create)
  appointmentTimeOverride?: Date | null;
  timezone: TimezoneAbbreviationEnum;
  isManualEmployeeAssignEnabled: Company['isManualEmployeeAssignEnabled'];
  dismissable?: boolean;
};

type HeldAppointmentCardProps = {
  appointmentDisplayType: 'Held';
  requestedTimeFieldIssueProps: FieldIssueProps;
} & BaseAppointmentCardProps;

type PreviousAppointmentCardProps = {
  appointmentDisplayType: 'Previous';
} & BaseAppointmentCardProps;

export type AppointmentCardProps = HeldAppointmentCardProps | PreviousAppointmentCardProps;

export const AppointmentCard = (props: AppointmentCardProps): React.ReactElement => {
  const {
    onCloseAppointment,
    onSetRequestedTimeMinutes,
    appointment,
    appointmentTimeOverride,
    timezone,
    appointmentDisplayType,
    isManualEmployeeAssignEnabled,
    dismissable: propsDismissable
  } = props;

  const [timeRange, setTimeRange] = useState(() => {
    const init: { minTime?: Date; maxTime?: Date; midnightArrivalVisible: boolean } = {
      minTime: undefined,
      maxTime: undefined,
      midnightArrivalVisible: false
    };
    return init;
  });

  useEffect(() => {
    if (!appointment) {
      setTimeRange({ minTime: undefined, maxTime: undefined, midnightArrivalVisible: false });
      return;
    }

    const appointmentDate = new Date(appointment.date);

    // if not today and all day, then requested time picker list should contain all intervals for a day unfiltered
    const isToday = DateUtils.isSame(new Date(), appointmentDate, 'day', { timezone });
    if (!isToday && appointment.appointmentWindow.blockType === BlockTypeEnum.ALL_DAY) {
      setTimeRange({
        minTime: undefined,
        maxTime: undefined,
        midnightArrivalVisible: true
      });
      return;
    }

    const minTime = getEarliestAppointmentDate(appointment.appointmentWindow, appointmentDate, timezone);
    const { showMidnightArrival, latestSelectableDate } = getLatestAppointmentDateInfo(
      appointment.appointmentWindow,
      appointmentDate,
      timezone
    );

    setTimeRange({
      minTime,
      maxTime: latestSelectableDate,
      midnightArrivalVisible: showMidnightArrival
    });
  }, [appointment, timezone]);

  const CustomActionBar = (props: PickersActionBarProps) => {
    const { className } = props || {};

    const { acceptValueChanges } = usePickerActionsContext();

    return (
      <DialogActions className={className}>
        <Button
          onClick={(_event) => {
            const eod = DateUtils.toEndOf(new Date(), 'day', { timezone });
            const midnightArrivalTime = DateUtils.setUnits(eod, { second: 0, millisecond: 0 });
            onSetRequestedTimeMinutes?.(midnightArrivalTime);
            acceptValueChanges();
          }}>
          Midnight Arrival
        </Button>
      </DialogActions>
    );
  };

  const renderNewRequestedTime = (requestedTimeFieldIssueProps: FieldIssueProps) => (
    <Box sx={{ marginTop: 2, marginBottom: 1 }}>
      <TimePicker
        minTime={timeRange.minTime}
        maxTime={timeRange.maxTime}
        label='Requested Time'
        timezone={timezone}
        value={appointmentTimeOverride ? appointmentTimeOverride : null}
        timeSteps={{ minutes: 30 }}
        skipDisabled
        slots={{
          actionBar: timeRange.midnightArrivalVisible ? CustomActionBar : undefined
        }}
        slotProps={{
          actionBar: { actions: timeRange.midnightArrivalVisible ? ['accept'] : [] },
          layout: { sx: { display: 'block' } }
        }}
        textFieldProps={{ error: requestedTimeFieldIssueProps, required: true }}
        onChange={(newValue) => {
          // NOTE midnight arrival button click does not trigger this onChange!
          if (newValue) onSetRequestedTimeMinutes?.(newValue);
        }}
      />
    </Box>
  );

  const renderPreviousRequestedTime = () => (
    <Box sx={{ marginTop: 2, marginBottom: 2 }}>
      <Typography variant='body1Bold' text='Requested Time' />
      <Typography text={DateUtils.format(appointment.date, DateFormatEnum.Time, { timezone, showTimezone: true })} />
    </Box>
  );

  const isPreviousAppointment = appointmentDisplayType === 'Previous';
  const dismissable = typeof propsDismissable === 'boolean' ? propsDismissable : true;
  return (
    <Box>
      <Card variant='outlined'>
        <Stack sx={{ margin: 0.75 }}>
          <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body1Bold' text={`${isPreviousAppointment ? 'PREVIOUS' : 'NEW'} APPOINTMENT`} />
            {dismissable && (
              <Tooltip title='Clear Appointment' placement='bottom'>
                <IconButton sx={{ padding: 0 }} onClick={onCloseAppointment}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Typography variant='body1Bold' text='Window Date &amp; Time' />
          <Box>
            <AppointmentWindowDisplay appointment={appointment} timezone={timezone} />
          </Box>
          {isPreviousAppointment
            ? renderPreviousRequestedTime()
            : renderNewRequestedTime(props.requestedTimeFieldIssueProps)}
          <Box>
            <Typography variant='body1Bold' text='Window Details' />
            <CompanyWindowDisplay
              includeTrades
              companyWindow={appointment.companyWindow as CompanyWindowListDTO} // TODO -> CompanyWindowStampedSchema min(1) to nonempty() in appointment repo schema
              isManualEmployeeAssignEnabled={isManualEmployeeAssignEnabled}
            />
          </Box>
          {isPreviousAppointment && (
            <Typography variant='body1Bold' text={`Job #${appointment?.job?.providerId}` || 'No Job Linked'} />
          )}
        </Stack>
      </Card>
    </Box>
  );
};
